import Document16 from '@carbon/icons-react/es/document/16';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { SpaceTabSkelton } from 'api/space';
import { Loading } from 'components/atoms/loading';
import { useCKMentionEvents } from 'hooks/use-ck-mention-events';
import { MentionType } from 'vendor/ckeditor/plugins/mention/types';
import { SuggestionComponentProps } from './mention-popup-content';
import { useMentions } from './mention-store';
import { SuggestionListItem } from './suggestion-list-item';
import { isItemMatching } from './utils';
import {
  useSelectFirstSuggestion,
  useSelectSuggestion,
} from './utils/selectSuggestion';

interface SpaceTabsMentionProps extends SuggestionComponentProps {
  data?: SpaceTabSkelton[];
  isLoading?: boolean;
}

enum SpaceTabMeta {
  Name = 'spaceTabTitle',
  Id = 'spaceTabId',
}

export const SpaceTabsSuggestions: FC<SpaceTabsMentionProps> = ({
  onSelect,
  query,
  activeTab,
  data,
  isLoading = false,
}) => {
  const spaceTabsData = data;
  const [spaceTabs, setSpaceTabs] = useState<SpaceTabSkelton[]>([]);
  const [contextState] = useMentions();
  const { activeSuggestion, editor } = contextState;

  const selectSuggestion = useSelectSuggestion(activeTab);
  const selectFirstSuggestion = useSelectFirstSuggestion(activeTab);

  const suggestionArgs = useMemo(() => {
    return {
      data: spaceTabs,
      type: MentionType.SpaceTab,
      name: SpaceTabMeta.Name,
      id: SpaceTabMeta.Id,
    };
  }, [spaceTabs]);
  const handleCKMentionEvents = useCallback(
    (e) => {
      if (e.name === 'mentionPopup:selectNext') {
        selectSuggestion({
          direction: 1,
          ...suggestionArgs,
        });
      }
      if (e.name === 'mentionPopup:selectPrevious') {
        selectSuggestion({
          direction: -1,
          ...suggestionArgs,
        });
      }
    },
    [selectSuggestion, suggestionArgs],
  );

  useCKMentionEvents(handleCKMentionEvents, editor);

  useEffect(() => {
    if (
      activeTab === MentionType.SpaceTab &&
      activeSuggestion?.type !== MentionType.SpaceTab
    ) {
      selectSuggestion({
        direction: 1,
        ...suggestionArgs,
      });
    }
  }, [suggestionArgs, selectSuggestion, activeTab, activeSuggestion]);

  useDeepCompareEffect(() => {
    const filteredSpaceTabs =
      spaceTabsData
        ?.filter((spaceTab) =>
          isItemMatching(`${spaceTab.spaceTabTitle}`, query),
        )
        .slice(0, 100) || [];

    setSpaceTabs(filteredSpaceTabs);
    const args = {
      data: filteredSpaceTabs,
      type: MentionType.SpaceTab,
      name: SpaceTabMeta.Name,
      id: SpaceTabMeta.Id,
    };

    selectFirstSuggestion(args);
  }, [spaceTabsData, query]);

  return (
    <div>
      {isLoading ? (
        <Loading />
      ) : (
        <FixedSizeList
          itemCount={spaceTabs.length}
          itemSize={40}
          height={258}
          width="100%"
        >
          {({ index, style }) => {
            const spaceTab = spaceTabs[index];

            return (
              <div style={style}>
                <SuggestionListItem
                  key={index}
                  name={spaceTab.spaceTabTitle}
                  avatar={<Document16 />}
                  objectId={spaceTab.spaceTabId}
                  projectId={spaceTab.project?.projectId}
                  spaceId={spaceTab.space?.spaceId}
                  type={MentionType.SpaceTab}
                  subType={spaceTab?.type}
                  onSelect={onSelect}
                />
              </div>
            );
          }}
        </FixedSizeList>
      )}
    </div>
  );
};
